/**
 * The block of cells a body holds on the tactical grid.
 *
 * A body is anchored at its north-west cell and spreads east and south, so a
 * medium creature at (4, 7) holds (4,7) (5,7) (4,8) (5,8) and is drawn centred
 * on the middle of that block rather than on its anchor cell. The server speaks
 * the same geometry, so what blocks a route here blocks it there too.
 */

/** Cells a body of the given span holds, anchored at (x, y). */
export function footprintCells(x, y, span = 1) {
  const side = spanOf({ span });
  const cells = [];
  for (let dy = 0; dy < side; dy += 1) {
    for (let dx = 0; dx < side; dx += 1) {
      cells.push({ x: x + dx, y: y + dy });
    }
  }
  return cells;
}

/** `x,y` keys of every cell a body holds — the form the reach maps are keyed by. */
export function footprintKeys(x, y, span = 1) {
  return footprintCells(x, y, span).map(cell => `${cell.x},${cell.y}`);
}

/** Span carried by a spawned object, defaulting to a single cell. */
export function spanOf(obj) {
  const span = Number(obj?.span);
  return Number.isFinite(span) && span >= 1 ? Math.floor(span) : 1;
}

/** Whether two anchored blocks share a cell. */
export function footprintsOverlap(a, b) {
  const spanA = spanOf(a);
  const spanB = spanOf(b);
  return (
    a.x < b.x + spanB &&
    b.x < a.x + spanA &&
    a.y < b.y + spanB &&
    b.y < a.y + spanA
  );
}

/** Whether a body anchored at (x, y) stands on the given cell. */
export function footprintCoversCell(x, y, span, cellX, cellY) {
  const side = spanOf({ span });
  return cellX >= x && cellX < x + side && cellY >= y && cellY < y + side;
}

/**
 * Cells closed to everyone else by the bodies on the board.
 *
 * `exclude` is the mover: its own cells are not walls to itself, or it could
 * never leave the ground it is standing on.
 */
export function occupiedKeys(objects, exclude = null) {
  const taken = new Set();
  (objects || []).forEach(obj => {
    if (exclude && obj.id === exclude.id) return;
    footprintKeys(obj.x, obj.y, spanOf(obj)).forEach(key => taken.add(key));
  });
  return taken;
}

/**
 * Whether a body of `span` could stand anchored at (x, y).
 *
 * A boss cannot stop where only its anchor cell is clear — every cell under its
 * bulk has to be free, so a 3x3 refuses a one-cell doorway that a standard
 * creature walks straight through.
 */
export function blockFits(x, y, span, isFree) {
  return footprintCells(x, y, span).every(cell => isFree(cell.x, cell.y));
}

/** Chebyshev gap between two blocks, measured body edge to body edge. */
export function cellsWithinReach(a, b) {
  const spanA = spanOf(a);
  const spanB = spanOf(b);
  const gapX = Math.max(b.x - (a.x + spanA - 1), a.x - (b.x + spanB - 1), 0);
  const gapY = Math.max(b.y - (a.y + spanA - 1), a.y - (b.y + spanB - 1), 0);
  return Math.max(gapX, gapY);
}
